<section>
  <header>
    <h1 auto="implementing-step-1-title">Implementing Phase</h1>
  </header>
  <div class="explanation-margin section-description">
    <div class="header-container">
      <div class="explanation-container">
        <p><strong>Design Implementation Plan</strong></p>
        <p>
          The Implementing Phase directs you through the necessary steps to convert planning into action. During this phase, you will create an Annual Implementation Plan that provides a detailed explanation of the activities associated with each Critical Initiative to address for the coming year.
        </p>
        <p>
          To create the Annual Implementation Plan, begin by identifying and adding the activities necessary for carrying out selected Critical Initiatives.
        </p>
        <p>
          Use the drop-down menu to narrow your list of Critical Initiative(s) you wish to review or update.
        </p>
      </div>
    </div>
    <div class="explanation-margin">
      <div class="download-guide-container">
        <img class="book-icon pointer" src="assets/images/download-guide-icon.svg" alt="book-icon" (click)="openGuide()">
        <span class="download-tagline pointer" (click)="openGuide()">Download the guide</span> to learn more about <strong>Design and Implementation Plan</strong>.
      </div>
      <div class="select-initiative-header"><strong>Review your Critical Initiatives displayed below:</strong></div>
      <div class="selection-container">
        <div class="input-error-wrap sans-margin">
          <div class="form-row">
            <div class="control-container">
              <app-priority-dropdown
                [showDates]="true"
                [singleSelection]="false"
                [availableOptions]="dropdownPriorityInitiatives"
                (valueChangedCallback)="dropdownValueChanged($event)"
                [styleClassAppends]="['default-selector-width']"
                defaultLabel="Select Critical Initiative(s)"
                selectedItemsLabel="Critical Initiatives Selected ({0})"
                selectAllItemsLabel="Select All Critical Initiatives"
                allItemsSelectedLabel="All Critical Initiatives Selected ({0})"
              ></app-priority-dropdown>
              <button class="btn btn-primary init-select-btn"
                      [disabled]="!filteredInitiativePriorities || filteredInitiativePriorities.length === 0 || !selectedOptions || selectedOptions.length === 0"
                      (click)="displaySelectedInitiatives()">View</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <ng-container *ngIf="filteredInitiativePriorities.length > 0">
    <div *ngFor="let priority of filteredInitiativePriorities">
      <div class="title-wrapper">
        <div class="priority-title" [style.background-color]="priority.colorCode">{{priority.title}}</div>
      </div>
      <ul class="initiative-list styleless">
        <ng-container *ngFor="let objective of priority.objectives">
          <ng-container *ngFor="let initiative of objective.initiatives; index as i;">
            <li [ngClass]="{'no-bottom-border': initiative.activities.length === 0 && i === 0}">
              <div class="initiative-container flex-initiative" [ngClass]="{'first-one': i === 0}"
                   [style.border-left-color]="getInitiativeBorderColor(priority.colorCode, i)">
                <div class="initiative-details">
                  <h4>Critical Initiative</h4>
                  <div class="text hard-break-text">{{initiative.description}}</div>
                  <div class="initiative-sub-details">
                    <div class="initiative-dates">
                      <span class="label"><b>Start Date:</b></span>
                      <span class="start-date"
                            *ngIf="initiative.startDate">{{initiative.startDate | date:'MM/dd/yyyy'}}</span>
                      <span class="label"><b>End Date:</b></span>
                      <span *ngIf="initiative.endDate">{{initiative.endDate | date:'MM/dd/yyyy'}}</span>
                    </div>
                    <div>
                      <span class="label"><b>Objective:</b></span><span>{{objective.text}}</span>
                    </div>
                  </div>
                </div>
                <div class="add-activity">
                  <button auto="add-activity-btn" [disabled]="isAddActivityDisabled(initiative)" (click)="onClickAddActivity(initiative)"
                          *ngIf="permissions.canEditBody" class="btn btn-primary">
                    Add Activity
                  </button>
                </div>
              </div>
              <div class="activities-container">
                <div *ngFor="let activity of initiative.activities; index as activityIndex"
                     class="activity-container"
                     [ngClass]="{'last-one': isLastActivity(initiative.activities, activityIndex)}">
                  <div class="flex-right-left">
                    <div>
                      <div class="activity-title" [style.border-bottom-color]="priority.colorCode">Activity</div>
                      <div class="activity-description">{{activity.description}}</div>
                    </div>
                    <div class="edit-delete-activity">
                      <ng-container *ngIf="permissions.canEditBody">
                        <i auto="edit-activity-btn" class="fa fa-pencil pointer" (click)="onClickEditActivity(activity, initiative)"></i>
                        <i auto="delete-activity-btn" class="fa fa-trash pointer" (click)="onClickDeleteActivity(activity)"></i>
                      </ng-container>
                    </div>
                  </div>
                  <div class="activity-main-content">
                    <div class="flex-right-left person-launch-date">
                      <div>
                        <b>Person Responsible:</b>{{activity.responsibleStakeHolder}}
                      </div>
                      <div>
                        <b>Launch Date:</b>{{activity.launchDate | date:'MM/dd/yyyy'}}
                      </div>
                    </div>
                    <div class="resources">
                      <div class="title"><b>Resource(s)</b></div>
                      <div class="resources-split-row-container">
                        <div class="financial-resources-data">
                          <span><strong>Financial Resources:</strong></span>&nbsp;{{activity.financialResources | currency:'USD':'symbol'}}
                        </div>
                        <div class="sources-of-funding-data hard-break-text">
                          <span><strong>Source of Funding:</strong></span>&nbsp;{{activity.sourceOfFunding}}
                        </div>
                      </div>
                      <div class="other-text-container hard-break-text">
                        <span><strong>Other:</strong></span>&nbsp;{{activity.otherResources}}
                      </div>
                    </div>
                    <div class="measures-and-targets">
                      <div class="measures no-border-left">
                        <div class="title"
                             [ngClass]="{'add-bottom-border': addMeasuresTitleBorder(activity)}">Activity Measure(s)
                        </div>
                        <ul class="styleless striped-list measure-target-list">
                          <li *ngIf="activity.measure && activity.measure.length" class="measure-list-item hard-break-text">{{activity.measure}}</li>
                        </ul>
                      </div>
                      <div class="targets no-border-right">
                        <div class="title"
                             [ngClass]="{'add-bottom-border': addTargetsTitleBorder(activity)}">Benchmark(s)</div>
                        <ul class="styleless striped-list measure-target-list">
                          <li *ngFor="let target of activity.activityTargets" class="target-list-item">
                            <div class="target-outcome break-text">{{target.expectedOutcome}}</div>
                            <div class="target-date"><b>Target: {{target.targetDate | date:'MM/dd/yyyy'}}</b></div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </li>
          </ng-container>
        </ng-container>
      </ul>
    </div>
  </ng-container>

  <ng-container *ngIf="(isLoading$ | async) === false">
    <ng-container *ngIf="!planHasPriorities">
      <div class="empty-message">
        <h1>
          <span class="text">To begin this subphase, complete the Envisioning phase first.</span>
        </h1>
      </div>
    </ng-container>
    <ng-container *ngIf="planHasPriorities && !planHasInitiatives">
      <div class="empty-message">
        <h1>
          <span class="text">In order to begin this subphase, complete the work in your previous subphases.</span>
        </h1>
      </div>
    </ng-container>
  </ng-container>

</section>

<p-dialog #confirmDeleteDialog styleClass="strategies-confirm-dialog" [modal]="true" [closable]="false" appendTo="body">
  <p-header>
    <img src="assets/images/exclamation.svg" alt="Alert">
    <span class="dialog-title">Are You Sure?</span>
  </p-header>
  <div>Deleting this will also impact any associated content.</div>
  <div><strong>Do you want to continue?</strong></div>
  <p-footer>
    <button auto="implementing-activity-cancel-btn" class="btn btn-alt" type="button" pButton label="Cancel"
            (click)="confirmDeleteDialog.visible = false"></button>
    <button auto="implementing-activeity-delete-btn" class="btn" type="button" pButton label="Delete" (click)="onConfirmDeleteDialogAccept()"></button>
  </p-footer>
</p-dialog>

<p-sidebar #activitySidebar
           [(visible)]="isActivityActive"
           appendTo="body"
           position="right"
           styleClass="activity-sidebar"
           (onShow)="activity.onShowSidebar(activitySidebar)"
           (onHide)="activity.onHideSidebar()">
  <app-activity #activity
                (create)="onActivityCreate($event)"
                (edit)="onActivityEdit($event)"
                (cancel)="isActivityActive = false">
  </app-activity>
</p-sidebar>
